import React, {useEffect, useMemo, useRef, useState} from "react";
import {OverlayPanel} from "primereact/overlaypanel";
import {useSelector} from "react-redux";
import ZofinoDatepicker from "../../../system/components/zofino-datepicker";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {dateToPersian} from "../../../system/utils";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import {
    FILTER_CATEGORIES,
    QUICK_FILTERS,
    categoryOptions,
    createTaskListFilters,
    filterTaskList,
    getTaskListFilterCount,
    toNativeDate,
} from "./task-filter-model";
import "./task-list-filter.css";

export {createTaskListFilters, filterTaskList, getTaskListFilterCount};

const EMPTY_RANGE = {start: null, end: null};

function formatRange(range) {
    const parts = [range?.start, range?.end].map(toNativeDate).filter(Boolean).map((date) => dateToPersian(date, "YYYY/MM/DD"));
    return parts.join(" تا ");
}

function chipValue(category, values, options, customRange) {
    if (values.length > 2) return `${values.length} مورد`;
    return values.map((value) => {
        if (value === "custom_date") return formatRange(customRange) || "انتخاب تاریخ";
        return options.find((option) => option.id === value)?.label ?? value;
    }).join("، ");
}

function TaskListFilter({filters: controlledFilters, onChange, onFiltersChange, tasks, deferred = false, calendarMode = false, calendarToolbarId, calendarPrimaryToolbarId, calendarSecondaryToolbarId, onDateRangeNavigate}) {
    const menuRef = useRef(null);
    const workspaceMemberships = useSelector((state) => state.app.workspaceMembershipData?.workspace?.workspace_memberships);
    const [internalFilters, setInternalFilters] = useState(createTaskListFilters);
    const filters = controlledFilters ?? internalFilters;
    const setFilters = (updater) => {
        const next = typeof updater === "function" ? updater(filters) : updater;
        if (controlledFilters === undefined) setInternalFilters(next);
        onFiltersChange?.(next);
    };

    // menu state: category list → one category's values (→ the custom date picker)
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState([]);
    const [draftRange, setDraftRange] = useState(EMPTY_RANGE);
    const [pickingDate, setPickingDate] = useState(false);
    const [query, setQuery] = useState("");
    const [pickerSession, setPickerSession] = useState(0);

    useEffect(() => {
        if (!deferred) onChange?.(filterTaskList(tasks, filters));
        // callback props intentionally have unstable identities
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deferred, filters, tasks]);

    const values = filters.values ?? {};
    const activeCategories = FILTER_CATEGORIES.filter((category) => values[category.id]?.length);
    const hasAny = Boolean(activeCategories.length || filters.quick);
    const editingCategory = FILTER_CATEGORIES.find((category) => category.id === editing);
    const options = useMemo(
        () => (editing ? categoryOptions(editing, tasks ?? [], workspaceMemberships ?? []) : []),
        [editing, tasks, workspaceMemberships],
    );
    const visibleOptions = query.trim()
        ? options.filter((option) => option.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()))
        : options;
    const visibleCategories = !editing && query.trim()
        ? FILTER_CATEGORIES.filter((category) => category.label.includes(query.trim()))
        : FILTER_CATEGORIES;

    const openCategory = (categoryId) => {
        setEditing(categoryId);
        setDraft([...(values[categoryId] ?? [])]);
        setDraftRange(filters.customRanges?.[categoryId] ?? EMPTY_RANGE);
        setPickingDate(false);
        setQuery("");
    };
    const openMenu = (event, categoryId = null) => {
        if (categoryId) openCategory(categoryId);
        else {
            setEditing(null);
            setPickingDate(false);
            setQuery("");
        }
        menuRef.current?.show(event, event.currentTarget);
    };
    const backToCategories = () => {
        setEditing(null);
        setPickingDate(false);
        setQuery("");
    };
    const toggleValue = (value) => {
        if (value === "custom_date" && !draft.includes(value)) {
            setPickerSession((session) => session + 1);
            setPickingDate(true);
            return;
        }
        setDraft((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
    };
    const applyFilter = () => {
        const nextValues = {...values};
        const nextRanges = {...(filters.customRanges ?? {})};
        if (draft.length) nextValues[editing] = draft;
        else delete nextValues[editing];
        if (draft.includes("custom_date")) nextRanges[editing] = draftRange;
        else delete nextRanges[editing];
        if (editing === "due_date" && draft.includes("custom_date")) {
            onDateRangeNavigate?.(toNativeDate(draftRange.start) ?? toNativeDate(draftRange.end));
        }
        setFilters((previous) => ({...previous, values: nextValues, customRanges: nextRanges}));
        menuRef.current?.hide();
    };
    const removeFilter = (event, categoryId) => {
        event.stopPropagation();
        setFilters((previous) => {
            const nextValues = {...(previous.values ?? {})};
            const nextRanges = {...(previous.customRanges ?? {})};
            delete nextValues[categoryId];
            delete nextRanges[categoryId];
            return {...previous, values: nextValues, customRanges: nextRanges};
        });
    };
    const clearAll = () => setFilters((previous) => ({...createTaskListFilters(), search: previous.search}));

    const renderOptionLabel = (option) => {
        if (editingCategory?.kind === "people") {
            return (
                <span className="flex align-items-center gap-2 min-w-0">
                    {option.person
                        ? <Avatar memberData={option.person} className="text-3xs" style={{width: "1.25rem", height: "1.25rem"}}/>
                        : <span className="zf-filter__none-avatar"><Icon icon="mi mi:person_off" className="text-2xs"/></span>}
                    <span className="zf-filter__ellipsis">{option.label}</span>
                </span>
            );
        }
        if (option.id === "custom_date" && draft.includes("custom_date")) {
            return <span className="zf-filter__ellipsis">{option.label}: {formatRange(draftRange)}</span>;
        }
        return (
            <span className="flex align-items-center gap-2 min-w-0">
                {option.color && <span className="zf-filter__dot" style={{background: option.color}}/>}
                {option.icon && editing === "task_type" && <Icon icon={`mi mi:${option.icon}`} className="text-sm text-500"/>}
                <span className="zf-filter__ellipsis">{option.label}</span>
            </span>
        );
    };

    const menuBody = () => {
        if (!editing) {
            return (
                <>
                    <div className="zf-filter__search">
                        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="جستجو فیلتر..." autoFocus/>
                    </div>
                    <div className="zf-filter__list">
                        {visibleCategories.map((category) => {
                            const count = values[category.id]?.length ?? 0;
                            return (
                                <button type="button" key={category.id} className={`zf-filter__category ${count ? "is-active" : ""}`} onClick={() => openCategory(category.id)}>
                                    <span className="flex align-items-center gap-2">
                                        <Icon icon={`mi mi:${category.icon}`} className="text-base zf-filter__category-icon"/>
                                        <span>{category.label}</span>
                                    </span>
                                    {count ? <span className="zf-filter__count">{count}</span> : <Icon icon="mi mi:chevron_left" className="text-sm zf-filter__chevron"/>}
                                </button>
                            );
                        })}
                    </div>
                </>
            );
        }
        if (pickingDate) {
            return (
                <div className="p-2">
                    <ZofinoDatepicker
                        value={draftRange}
                        config={{calendar: persian, locale: persian_fa, range: true}}
                        sessionKey={pickerSession}
                        onApply={({value}) => {
                            const range = value ?? EMPTY_RANGE;
                            const hasValue = Boolean(toNativeDate(range.start) || toNativeDate(range.end));
                            setDraftRange(range);
                            setDraft((current) => hasValue
                                ? [...current.filter((item) => item !== "custom_date"), "custom_date"]
                                : current.filter((item) => item !== "custom_date"));
                            setPickingDate(false);
                        }}
                        onCancel={() => setPickingDate(false)}
                        readOnlyInput
                    />
                </div>
            );
        }
        return (
            <>
                {editingCategory?.searchable && (
                    <div className="zf-filter__search">
                        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="جستجو..." autoFocus/>
                    </div>
                )}
                <div className="zf-filter__list">
                    {visibleOptions.length === 0 && <div className="zf-filter__empty">موردی پیدا نشد</div>}
                    {visibleOptions.map((option) => {
                        const checked = draft.includes(option.id);
                        return (
                            <button type="button" role="checkbox" aria-checked={checked} key={option.id} className={`zf-filter__option ${checked ? "is-checked" : ""}`} onClick={() => toggleValue(option.id)}>
                                <span className="zf-filter__checkbox">{checked && <i className="pi pi-check"/>}</span>
                                <span className="flex-1 min-w-0 flex align-items-center justify-content-between gap-2">
                                    {renderOptionLabel(option)}
                                    {editingCategory?.kind === "date" && <Icon icon={`mi mi:${option.icon}`} className="text-sm text-400 flex-none"/>}
                                </span>
                            </button>
                        );
                    })}
                </div>
                <div className="zf-filter__footer">
                    <button type="button" className="zf-filter__apply" onClick={applyFilter}>اعمال فیلتر</button>
                </div>
            </>
        );
    };

    return (
        <div className={`zofino-task-list-filters zf-filter flex flex-column w-full p-2 md:p-3 gap-2 ${calendarMode ? "zofino-calendar-filters" : ""}`} dir="rtl">
            <div className={`flex flex-wrap align-items-center gap-2 w-full ${calendarMode ? "zofino-calendar-filters__primary" : ""}`}>
                <div className="zf-filter__quick" role="group" aria-label="فیلتر سریع">
                    {QUICK_FILTERS.map((quick) => (
                        <button
                            type="button"
                            key={quick.id}
                            aria-pressed={filters.quick === quick.id}
                            className={`zf-filter__quick-button ${filters.quick === quick.id ? "is-active" : ""}`}
                            onClick={() => setFilters((previous) => ({...previous, quick: previous.quick === quick.id ? null : quick.id}))}
                        >
                            {quick.label}
                        </button>
                    ))}
                </div>
                <span className="zf-filter__separator"/>

                {activeCategories.map((category) => {
                    const categoryValues = values[category.id];
                    const label = chipValue(category.id, categoryValues, categoryOptions(category.id, tasks ?? [], workspaceMemberships ?? []), filters.customRanges?.[category.id]);
                    return (
                        <div key={category.id} role="button" tabIndex={0} className="zf-filter__chip" onClick={(event) => openMenu(event, category.id)} onKeyDown={(event) => event.key === "Enter" && openMenu(event, category.id)}>
                            <span className="zf-filter__chip-label">{category.label}:</span>
                            <span className="zf-filter__chip-value">{label}</span>
                            <button type="button" aria-label={`حذف فیلتر ${category.label}`} className="zf-filter__chip-remove" onClick={(event) => removeFilter(event, category.id)}>
                                <Icon icon="mi mi:close" className="text-xs"/>
                            </button>
                        </div>
                    );
                })}

                <button type="button" className="zf-filter__add" onClick={(event) => openMenu(event)}>
                    <Icon icon="mi mi:add" className="text-base"/> افزودن فیلتر
                </button>
                {hasAny && <button type="button" className="zf-filter__clear" onClick={clearAll}>پاک کردن همه</button>}

                {calendarPrimaryToolbarId && <div id={calendarPrimaryToolbarId} className="zofino-calendar-toolbar-slot zofino-calendar-toolbar-slot--primary"/>}

                <label className="zf-filter__text-search">
                    <i className="pi pi-search"/>
                    <input
                        value={filters.search ?? ""}
                        placeholder={calendarMode ? "جستجو در تقویم..." : "جستجوی سریع"}
                        onChange={(event) => setFilters((previous) => ({...previous, search: event.target.value}))}
                    />
                </label>
                {calendarToolbarId && <div id={calendarToolbarId} className="zofino-calendar-toolbar-slot"/>}
            </div>
            {calendarSecondaryToolbarId && (
                <div className="flex flex-wrap align-items-center gap-2 zofino-calendar-filters__secondary">
                    <div id={calendarSecondaryToolbarId} className="zofino-calendar-toolbar-slot zofino-calendar-toolbar-slot--secondary"/>
                </div>
            )}

            <OverlayPanel ref={menuRef} className={`zf-filter-menu rtl ${pickingDate ? "is-wide" : ""}`} onHide={() => setPickingDate(false)}>
                <div dir="rtl" className="shabnam-font">
                    {editing && (
                        <div className="zf-filter__header">
                            <button type="button" aria-label="بازگشت" className="zf-filter__back" onClick={pickingDate ? () => setPickingDate(false) : backToCategories}>
                                <Icon icon="mi mi:arrow_forward" className="text-base"/>
                            </button>
                            <span>{pickingDate ? "انتخاب تاریخ" : editingCategory?.label}</span>
                        </div>
                    )}
                    {menuBody()}
                </div>
            </OverlayPanel>
        </div>
    );
}

export default TaskListFilter;
